import { useEffect, useMemo, useState } from 'react'
import { supabase } from './supabase'
import { hasAnyKind } from './kinds'
import { isLiveNow, type LiveWindow } from './seasonal'
import type { EventRow, FilterGroup } from '../types'

export { INTERESTS, type Interest } from './interests'

// ── Feeding "✨ New!" from the digest ─────────────────────────────────────────
// The digest pipeline already turns up new businesses (the new-business pull and
// the Vibemap one). When a grand opening comes out of that, it becomes a row in
// lyh_filter_groups rather than anything in this file:
//
//   insert into lyh_filter_groups (slug, label, blurb, hosts, name_match,
//                                  show_from, show_until)
//   values ('new', '✨ New!', 'Grand openings and first weeks.', '{}',
//           'Wildflower Bakery|Hill City Hardware',
//           now(), now() + interval '30 days')
//   on conflict (slug) do update
//     set name_match = excluded.name_match,
//         show_until = excluded.show_until;
//
// One row, reused. Each new opening is OR-ed into name_match and show_until is
// pushed out again, so the chip covers whatever is currently new and retires by
// itself when the last one ages out - nobody has to remember to delete it.
//
// NAME THE BUSINESSES in name_match. Not "grand opening|now open": a generic
// pattern is the same live-keyword mistake measured in lib/seasonal, where a
// themed name gets read as the thing itself. The row IS the curation, so what
// goes in it should be the actual names a person confirmed.
//
// A window is what makes a group rotating, so anything else that should come and
// go - a festival week, a fair - is the same shape with its own slug.

/**
 * Curated groups that are live RIGHT NOW - the ones with a show_until on them.
 *
 * A window is what makes a group rotating rather than standing. "✨ New!" is the
 * first: a grand opening stops being new, so the row carries the date it should
 * stop being offered and disappears on its own rather than waiting for someone
 * to remember it. The permanent groups (the Reddit and Facebook thread answers)
 * have no window and are deliberately NOT returned here - they're reached by
 * their links, not by a chip in the drawer.
 *
 * get_filter_group already refuses an out-of-window slug, so this is the reading
 * half of the same rule: that one stops an expired LINK resolving, this one stops
 * an expired chip being offered.
 *
 * A failed query returns an empty list, which shows no chips rather than stale
 * ones. Nothing else in the drawer depends on it.
 */
export function useLiveGroups(now: Date): FilterGroup[] {
  const [groups, setGroups] = useState<FilterGroup[]>([])
  // Pass a pinned date, or this refetches on every render.
  const iso = now.toISOString()

  useEffect(() => {
    let cancelled = false
    void supabase
      .from('lyh_filter_groups')
      // show_from is checked below rather than in the query: a null show_from
      // means "live from the moment it existed", and a `lte` comparison against
      // NULL drops the row instead of keeping it.
      .select('slug,label,blurb,hosts,name_match,days,source_url,show_from')
      .not('show_until', 'is', null)
      .gte('show_until', iso)
      .then(({ data, error }) => {
        if (cancelled) return
        if (error) {
          console.error('load live groups', error)
          return
        }
        const rows = (data ?? []) as (FilterGroup & LiveWindow)[]
        setGroups(rows.filter((r) => isLiveNow(r, new Date(iso))))
      })
    return () => {
      cancelled = true
    }
  }, [iso])

  return groups
}

/**
 * Load a named filter group (?group=anime) from lyh_filter_groups.
 *
 * An unknown slug resolves to null and the caller shows everything. These links
 * get pasted into public comments that outlive any single group, so a retired or
 * mistyped slug must degrade to the full calendar, never to an empty one.
 */
export function useFilterGroup(slug: string | null) {
  const [group, setGroup] = useState<FilterGroup | null>(null)
  const [loading, setLoading] = useState(slug !== null)

  useEffect(() => {
    let cancelled = false
    if (!slug) {
      setGroup(null)
      setLoading(false)
      return
    }
    setLoading(true)
    // RPC rather than a plain select: it also bumps last_used_at, which is what
    // keeps an actively-followed link alive against the idle sweep. It's the only
    // write path anon has to this table.
    void supabase
      .rpc('get_filter_group', { p_slug: slug })
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return
        if (error) console.error('load filter group', error)
        setGroup((data as FilterGroup | null) ?? null)
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [slug])

  // Compiled once per group. A malformed pattern degrades to "no name rule"
  // rather than throwing on every render and taking the calendar down with it.
  const nameRe = useMemo(() => {
    if (!group?.name_match) return null
    try {
      return new RegExp(group.name_match, 'i')
    } catch {
      console.error('filter group has an invalid name_match', group.slug)
      return null
    }
  }, [group])

  // A group with neither rule can't filter anything, so treat it as absent.
  // `days` alone doesn't count: it narrows a set, it can't define one.
  const active = group !== null && (group.hosts.length > 0 || nameRe !== null)

  return { group: active ? group : null, nameRe, loading }
}

/**
 * (any kind ∈ hosts OR name matches) AND (day is allowed) - "kind" is the host
 * OR a tag (lib/kinds), so a Comedy group picks up the open mic tagged Comedy.
 *
 * The first half is a union so a topic can be picked up wherever it's filed; the
 * day rule NARROWS that result rather than joining the union, or a weekend group
 * would drag in every event that happens to fall on a Saturday.
 */
export function matchesGroup(
  e: EventRow,
  group: FilterGroup,
  nameRe: RegExp | null,
): boolean {
  const inSet =
    hasAnyKind(e, group.hosts) ||
    (nameRe !== null && nameRe.test(e.name))
  if (!inSet) return false

  if (group.days && group.days.length > 0) {
    // JS getDay() is 0=Sun..6=Sat; the column stores ISO 1=Mon..7=Sun.
    const js = new Date(e.start_time).getDay()
    if (!group.days.includes(js === 0 ? 7 : js)) return false
  }
  return true
}

const DAY_NAMES = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

/**
 * Day constraint as a single facet word for the Includes list: "Weekends",
 * "Weekdays", or "Sat, Sun". Null when unrestricted.
 *
 * A plain noun, not "Weekends only" - the banner lists it alongside the
 * categories as another thing the view includes, and the editorial "only" read
 * like copy a person wouldn't bother writing.
 */
export function describeDays(days: number[] | null): string | null {
  if (!days || days.length === 0 || days.length === 7) return null
  const set = [...days].sort((a, b) => a - b)
  const key = set.join(',')
  if (key === '6,7') return 'Weekends'
  if (key === '1,2,3,4,5') return 'Weekdays'
  return set.map((d) => DAY_NAMES[d] ?? '?').join(', ')
}
